import { useState } from "react";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, AlertTriangle } from "lucide-react";

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const FIELDS = [
  ["gross", "Gross revenue"],
  ["vendorFee", "Vendor fee"],
  ["labor", "Labor cost"],
  ["cogs", "COGS"],
];

const Scorecard = ({ card, onChange, onDelete }) => {
  const values = FIELDS.map(([k]) => num(card[k]));
  const complete = values.every((v) => v !== null && !Number.isNaN(v));
  const net = complete ? values[0] - (values[1] + values[2] + values[3]) : null;
  const margin = complete && values[0] > 0 ? ((net / values[0]) * 100).toFixed(1) : null;

  return (
    <div className="border border-border bg-[#141414] rounded-sm p-5" data-testid={`scorecard-${card.id}`}>
      <div className="flex items-center gap-3">
        <Input
          data-testid={`scorecard-name-${card.id}`}
          value={card.name}
          onChange={(e) => onChange({ ...card, name: e.target.value })}
          className="rounded-sm bg-[#0A0A0A] display text-xl uppercase"
          placeholder="Event name"
        />
        <button onClick={onDelete} data-testid={`scorecard-delete-${card.id}`} className="text-muted-foreground hover:text-destructive">
          <Trash2 size={16} />
        </button>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        {FIELDS.map(([k, label]) => (
          <div key={k}>
            <Label className="mono text-[10px] tracking-widest text-muted-foreground">
              {label.toUpperCase()}
            </Label>
            <Input
              data-testid={`scorecard-${k}-${card.id}`}
              type="number"
              value={card[k] ?? ""}
              onChange={(e) => onChange({ ...card, [k]: e.target.value })}
              className="mono mt-1 rounded-sm bg-[#0A0A0A]"
              placeholder="0"
            />
          </div>
        ))}
      </div>
      <div className="mt-4 border-t border-border pt-4" data-testid={`scorecard-result-${card.id}`}>
        {complete ? (
          <div className="flex flex-wrap items-baseline gap-6">
            <div>
              <div className="mono text-[10px] tracking-widest text-muted-foreground">NET PROFIT</div>
              <div
                className={`display text-4xl ${net >= 0 ? "text-[#34C759]" : "text-destructive"}`}
                data-testid={`scorecard-net-${card.id}`}
              >
                ${net.toFixed(2)}
              </div>
            </div>
            <div>
              <div className="mono text-[10px] tracking-widest text-muted-foreground">MARGIN</div>
              <div className="display text-4xl text-primary">{margin}%</div>
            </div>
          </div>
        ) : (
          <p className="mono text-xs text-muted-foreground">
            Enter gross, vendor fee, labor and COGS to calculate profitability.
          </p>
        )}
      </div>
    </div>
  );
};

export const EventPrepTab = () => {
  const { content, state, updateState } = useData();
  const [prepDraft, setPrepDraft] = useState({ name: "", date: "", covers: "" });
  const checklist = content.checklist || [];
  const stages = [...new Set(checklist.map((c) => c.stage))];
  const checked = state.checklist || [];

  const toggleItem = (id) =>
    updateState((prev) => {
      const list = prev.checklist || [];
      return {
        ...prev,
        checklist: list.includes(id) ? list.filter((x) => x !== id) : [...list, id],
      };
    });

  const addPrep = () => {
    if (!prepDraft.name) return;
    updateState((prev) => ({
      ...prev,
      eventPreps: [...(prev.eventPreps || []), { id: `prep-${Date.now()}`, ...prepDraft }],
    }));
    setPrepDraft({ name: "", date: "", covers: "" });
  };

  const addScorecard = () =>
    updateState((prev) => ({
      ...prev,
      scorecards: [
        ...(prev.scorecards || []),
        { id: `sc-${Date.now()}`, name: "", gross: "", vendorFee: "", labor: "", cogs: "" },
      ],
    }));

  return (
    <div className="space-y-8" data-testid="event-prep-tab">
      <div className="border border-border bg-[#141414] rounded-sm p-6">
        <h3 className="display text-3xl uppercase">First event checklist</h3>
        <p className="text-sm text-muted-foreground">Milestone 9 closes when every box is ticked.</p>
        <div className="mt-5 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {stages.map((stage) => (
            <div key={stage}>
              <div className="mono text-[10px] tracking-[0.3em] text-primary">{stage.toUpperCase()}</div>
              <div className="mt-3 space-y-2">
                {checklist
                  .filter((c) => c.stage === stage)
                  .map((c) => (
                    <label key={c.id} className="flex items-start gap-3 text-sm cursor-pointer">
                      <Checkbox
                        data-testid={`checklist-item-${c.id}`}
                        checked={checked.includes(c.id)}
                        onCheckedChange={() => toggleItem(c.id)}
                        className="mt-0.5 rounded-sm"
                      />
                      <span className={checked.includes(c.id) ? "text-muted-foreground line-through" : ""}>
                        {c.item}
                      </span>
                    </label>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-border bg-[#141414] rounded-sm p-6">
        <h3 className="display text-3xl uppercase">Event entries</h3>
        <div className="mt-4 flex flex-col gap-3 md:flex-row">
          <Input
            data-testid="prep-name-input"
            value={prepDraft.name}
            onChange={(e) => setPrepDraft({ ...prepDraft, name: e.target.value })}
            placeholder="Event name"
            className="rounded-sm bg-[#0A0A0A]"
          />
          <Input
            data-testid="prep-date-input"
            type="date"
            value={prepDraft.date}
            onChange={(e) => setPrepDraft({ ...prepDraft, date: e.target.value })}
            className="rounded-sm bg-[#0A0A0A] md:w-48"
          />
          <Input
            data-testid="prep-covers-input"
            type="number"
            value={prepDraft.covers}
            onChange={(e) => setPrepDraft({ ...prepDraft, covers: e.target.value })}
            placeholder="Expected covers"
            className="mono rounded-sm bg-[#0A0A0A] md:w-48"
          />
          <Button
            data-testid="prep-add-btn"
            onClick={addPrep}
            className="rounded-sm bg-primary text-primary-foreground display text-lg uppercase hover:bg-[#E6C800]"
          >
            Add event
          </Button>
        </div>

        <div className="mt-5 space-y-3">
          {(state.eventPreps || []).map((p) => (
            <div
              key={p.id}
              data-testid={`prep-${p.id}`}
              className="flex flex-wrap items-center gap-4 border border-border/70 px-4 py-3"
            >
              <div className="display text-2xl uppercase">{p.name}</div>
              <div className="mono text-xs text-muted-foreground">{p.date || "no date"}</div>
              <div className="mono text-xs text-muted-foreground">{p.covers || "?"} covers</div>
              <div className="mono ml-auto flex items-center gap-2 text-[10px] text-primary">
                <AlertTriangle size={13} /> INGREDIENT + STAFFING: NEEDS SMASH HQ INPUT
              </div>
              <button
                data-testid={`prep-delete-${p.id}`}
                onClick={() =>
                  updateState((prev) => ({
                    ...prev,
                    eventPreps: prev.eventPreps.filter((x) => x.id !== p.id),
                  }))
                }
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          {(state.eventPreps || []).length === 0 && (
            <p className="text-sm text-muted-foreground">No events yet.</p>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <h3 className="display text-3xl uppercase">Event scorecards</h3>
          <Button
            data-testid="scorecard-add-btn"
            onClick={addScorecard}
            variant="outline"
            className="rounded-sm border-primary text-primary display text-lg uppercase hover:bg-primary hover:text-primary-foreground"
          >
            New scorecard
          </Button>
        </div>
        <div className="mt-4 space-y-4">
          {(state.scorecards || []).map((card) => (
            <Scorecard
              key={card.id}
              card={card}
              onChange={(next) =>
                updateState((prev) => ({
                  ...prev,
                  scorecards: prev.scorecards.map((c) => (c.id === next.id ? next : c)),
                }))
              }
              onDelete={() =>
                updateState((prev) => ({
                  ...prev,
                  scorecards: prev.scorecards.filter((c) => c.id !== card.id),
                }))
              }
            />
          ))}
          {(state.scorecards || []).length === 0 && (
            <p className="text-sm text-muted-foreground">
              No scorecards yet. Add one after your first event.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
